import { isTransferDeed } from './assessment-reconciliation';
import { documentTime } from './record-dates';
import type { RecorderDocument } from './types';

const release =
  /RELEASE.*LIEN|SATISFACTION.*LIEN|DISCHARGE.*LIEN|(?:CNCL|CANCEL(?:ATION|LATION|ED|LED)?)\s+(?:OF\s+)?LIEN/i;

export function isLienReleaseTitle(title: string) {
  return release.test(title);
}

export type LienReleaseMatch = 'explicit' | 'unique-parties' | 'balanced-batch' | 'ai-inferred';

export type GroupedLien = {
  document: RecorderDocument;
  release: RecorderDocument | null;
  batchReleases: RecorderDocument[];
  match: LienReleaseMatch | null;
  /** For `ai-inferred`: how sure the model was of the pairing. */
  confidence?: LienAiConfidence;
};
export function isActionableLien(document: RecorderDocument) {
  if (document.titles.some(isLienReleaseTitle)) return false;
  return document.titles.some(
    (title) => /LIEN/i.test(title) && !/DEED OF TRUST|MORTGAGE|ASSIGNMENT|SUBORDINAT/i.test(title),
  );
}

/**
 * The Recorder indexes the same agency and owner under different spellings
 * over the years ("SFPH", "SF Department of Public Health"; "CHOY RAYMOND G &
 * LORRAINE J", "RAYMOND G & LORRAINE J CHOY RE"), so compare canonical names.
 */
const AGENCY_ALIASES: Array<[RegExp, string]> = [
  [/PUBLIC\s*HEALTH|^SF\s*D?PH$|^D?PH$/, 'AGENCY:PUBLIC-HEALTH'],
  [/\bWATER\b|^SFWD$|^SFPUC$|PUBLIC\s*UTILITIES/, 'AGENCY:WATER'],
  [/TAX\s*COLLECTOR|TREASURER|^SFTTX$/, 'AGENCY:TREASURER'],
  [/BUILDING\s*INSPECTION|^SF\s*DBI$|^DBI$/, 'AGENCY:DBI'],
];
const OWNER_NOISE = new Set(['RE', 'ET', 'AL', 'ETAL', 'TR', 'TRS', 'TTEE', 'TTEES']);

function partyName(name: string) {
  const upper = name
    .replace(/^\([RE]\)\s*/, '')
    .toUpperCase()
    .replace(/^"|"$/g, '')
    .trim();
  const agency = AGENCY_ALIASES.find(([pattern]) => pattern.test(upper));
  if (agency) return agency[1];
  return [...new Set(upper.split(/[^A-Z0-9]+/).filter((part) => part && !OWNER_NOISE.has(part)))]
    .sort()
    .join(' ');
}

function parties(document: RecorderDocument) {
  return [...new Set(document.names.map(partyName).filter(Boolean))].sort().join('|');
}
function date(document: RecorderDocument) {
  const value = Date.parse(document.documentDate);
  return Number.isFinite(value) ? value : null;
}
function references(document: RecorderDocument) {
  return Object.entries(document.fields)
    .filter(([key]) => /refer|original|related|cross.?reference/i.test(key))
    .map(([, value]) => value)
    .join(' ');
}

function compareDocuments(a: RecorderDocument, b: RecorderDocument) {
  const aDate = date(a);
  const bDate = date(b);
  if (aDate === null || bDate === null) {
    if (aDate === bDate) return a.documentNumber.localeCompare(b.documentNumber);
    return aDate === null ? 1 : -1;
  }
  return aDate - bDate || a.documentNumber.localeCompare(b.documentNumber);
}

function isChronologicallyEligible(lien: RecorderDocument, released: RecorderDocument) {
  return date(lien) !== null && date(released) !== null && compareDocuments(lien, released) <= 0;
}

/**
 * Prefer exact source references, then unique party matches. If the remaining
 * same-party liens and releases balance one-for-one chronologically, close the
 * batch without claiming that any individual pairing came from the Recorder.
 */
export function groupLiens(documents: RecorderDocument[], aiMatches: LienAiMatch[] = []) {
  const uniqueDocuments = [
    ...new Map(documents.map((item) => [item.documentNumber || item.id, item])).values(),
  ];
  const liens: GroupedLien[] = uniqueDocuments.filter(isActionableLien).map((document) => ({
    document,
    release: null,
    batchReleases: [],
    match: null,
  }));
  const releases = uniqueDocuments
    .filter((d) => d.titles.some(isLienReleaseTitle))
    .sort(compareDocuments);
  const unmatchedReleases: RecorderDocument[] = [];

  for (const released of releases) {
    const statedReferences = references(released);
    const explicit = liens.filter(
      (l) =>
        !l.release &&
        !l.batchReleases.length &&
        l.document.documentNumber &&
        statedReferences.split(/[^A-Za-z0-9]+/).includes(l.document.documentNumber),
    );
    const candidates = liens.filter(
      (l) =>
        !l.release && !l.batchReleases.length && isChronologicallyEligible(l.document, released),
    );
    const sameParties = candidates.filter(
      (l) =>
        parties(l.document) &&
        parties(l.document).includes('|') &&
        parties(l.document) === parties(released),
    );
    // A stated reference outranks a party-name heuristic, even when the
    // referenced lien is absent from this parcel's available documents.
    const matches = statedReferences.trim() ? explicit : sameParties;
    if (matches.length === 1) {
      matches[0]!.release = released;
      matches[0]!.match = statedReferences.trim() ? 'explicit' : 'unique-parties';
    } else if (!statedReferences.trim() && date(released) !== null) {
      unmatchedReleases.push(released);
    }
  }

  // Walk each party pair's remaining liens and releases in recording order.
  // Whenever the releases so far close every open lien, that run balances and
  // is reconciled as one batch; a release with no earlier open lien belongs to
  // a lien outside this index and is skipped.
  const partyKeys = new Set(unmatchedReleases.map(parties));
  for (const partyKey of partyKeys) {
    if (!partyKey.includes('|')) continue;
    const events = [
      ...liens
        .filter(
          (lien) =>
            !lien.release &&
            !lien.batchReleases.length &&
            date(lien.document) !== null &&
            parties(lien.document) === partyKey,
        )
        .map((lien) => ({ document: lien.document, lien })),
      ...unmatchedReleases
        .filter((released) => parties(released) === partyKey)
        .map((document) => ({ document, lien: null })),
    ].sort((a, b) => compareDocuments(a.document, b.document));
    let open: GroupedLien[] = [];
    let run: GroupedLien[] = [];
    let runReleases: RecorderDocument[] = [];
    for (const event of events) {
      if (event.lien) {
        open.push(event.lien);
        run.push(event.lien);
        continue;
      }
      if (!open.length) continue;
      open = open.slice(1);
      runReleases.push(event.document);
      if (open.length) continue;
      const batch = runReleases;
      run.forEach((lien) => {
        lien.batchReleases = batch;
        lien.match = 'balanced-batch';
      });
      run = [];
      runReleases = [];
    }
  }
  // Model pairings fill only what the deterministic passes left open.
  const byKey = new Map(uniqueDocuments.map((item) => [documentKey(item), item]));
  for (const match of aiMatches) {
    const lien = liens.find(
      (item) =>
        documentKey(item.document) === match.lien && !item.release && !item.batchReleases.length,
    );
    const released = byKey.get(match.release);
    if (!lien || !released) continue;
    lien.release = released;
    lien.match = 'ai-inferred';
    lien.confidence = match.confidence;
  }
  return liens;
}

function documentKey(document: RecorderDocument) {
  return document.documentNumber || document.id;
}

export type LienAiConfidence = 'high' | 'medium';
/** One Recorder document as supplied to the lien-matching prompt. */
export type LienAiDocument = {
  key: string;
  role: 'lien' | 'release' | 'deed';
  date: string;
  titles: string[];
  names: string[];
};
export type LienAiMatch = {
  lien: string;
  release: string;
  confidence: LienAiConfidence;
  basis: string;
};

/**
 * Liens the deterministic passes could not pair, the releases no pass used,
 * and the ownership transfers around them. Empty when there is nothing the
 * model could pair.
 */
export function lienMatchCandidates(documents: RecorderDocument[]): LienAiDocument[] {
  const grouped = groupLiens(documents);
  const open = grouped.filter((lien) => !lien.release && !lien.batchReleases.length);
  if (!open.length) return [];
  const used = new Set(
    grouped.flatMap((lien) => [
      ...(lien.release ? [documentKey(lien.release)] : []),
      ...lien.batchReleases.map(documentKey),
    ]),
  );
  const unique = [...new Map(documents.map((item) => [documentKey(item), item])).values()];
  const releases = unique.filter(
    (item) => item.titles.some(isLienReleaseTitle) && !used.has(documentKey(item)),
  );
  if (!releases.length) return [];
  const item = (document: RecorderDocument, role: LienAiDocument['role']): LienAiDocument => ({
    key: documentKey(document),
    role,
    date: document.documentDate,
    titles: document.titles,
    names: document.names,
  });
  return [
    ...open.map((lien) => ({ document: lien.document, role: 'lien' as const })),
    ...releases.map((document) => ({ document, role: 'release' as const })),
    ...unique.filter(isTransferDeed).map((document) => ({ document, role: 'deed' as const })),
  ]
    .sort((a, b) => compareDocuments(a.document, b.document))
    .map(({ document, role }) => item(document, role));
}

/**
 * The model only proposes pairings. Each must name a supplied open lien and
 * an unused release recorded on or after it with a party in common, and no
 * lien or release may be used twice.
 */
export function validateLienMatches(
  proposals: unknown,
  candidates: LienAiDocument[],
): LienAiMatch[] {
  if (!Array.isArray(proposals)) return [];
  const byKey = new Map(candidates.map((item) => [item.key, item]));
  const usedLiens = new Set<string>();
  const usedReleases = new Set<string>();
  const accepted: LienAiMatch[] = [];
  for (const proposal of proposals) {
    if (!proposal || typeof proposal !== 'object') continue;
    const value = proposal as Record<string, unknown>;
    const lien = typeof value.lien === 'string' ? byKey.get(value.lien) : null;
    const released = typeof value.release === 'string' ? byKey.get(value.release) : null;
    const confidence = value.confidence;
    const basis = typeof value.basis === 'string' ? value.basis.trim() : '';
    if (
      lien?.role !== 'lien' ||
      released?.role !== 'release' ||
      (confidence !== 'high' && confidence !== 'medium') ||
      !basis ||
      usedLiens.has(lien.key) ||
      usedReleases.has(released.key)
    )
      continue;
    const lienTime = documentTime(lien.date);
    const releaseTime = documentTime(released.date);
    if (lienTime === null || releaseTime === null || releaseTime < lienTime) continue;
    const releaseParties = new Set(released.names.map(partyName));
    if (!lien.names.map(partyName).some((name) => releaseParties.has(name))) continue;
    usedLiens.add(lien.key);
    usedReleases.add(released.key);
    accepted.push({
      lien: lien.key,
      release: released.key,
      confidence,
      basis: basis.slice(0, 240),
    });
  }
  return accepted;
}

function isAgencyName(name: string) {
  return partyName(name).startsWith('AGENCY:');
}

/** The latest arm's-length deed: who owns the property now, per the Recorder. */
function latestSale(documents: RecorderDocument[]) {
  let latest: { time: number; document: RecorderDocument } | null = null;
  for (const document of documents) {
    if (!isTransferDeed(document) || isIntraFamilyTransfer(document)) continue;
    const time = documentTime(document.documentDate);
    if (time !== null && (!latest || time > latest.time)) latest = { time, document };
  }
  return latest?.document ?? null;
}

/**
 * A lien that names none of the buyers on the latest sale was recorded
 * against a previous owner, often after the sale when a city bill caught
 * up with the old owner.
 */
export function isPriorOwnerLien(lien: RecorderDocument, documents: RecorderDocument[]) {
  const sale = latestSale(documents);
  if (!sale) return false;
  const owners = new Set(sale.names.filter((name) => name.startsWith('(E)')).flatMap(surnames));
  const debtors = lien.names.filter((name) => !isAgencyName(name)).flatMap(surnames);
  return owners.size > 0 && debtors.length > 0 && !debtors.some((part) => owners.has(part));
}

const NAME_NOISE = new Set([
  'TRUST',
  'TRUSTEE',
  'TRUSTEES',
  'REVOC',
  'REVOCABLE',
  'LIVING',
  'LVG',
  'LV',
  'TR',
  'FAMILY',
  'FAM',
  'THE',
  'OF',
  'AND',
  'DECLARATION',
  'ETAL',
  'SURVIVORS',
  // Legal-entity suffixes and generic company words: shared by unrelated
  // parties, so they cannot establish an intra-family transfer.
  'LLC',
  'LLLP',
  'LLP',
  'PLLC',
  'LTD',
  'INC',
  'CORP',
  'CORPORATION',
  'COMPANY',
  'ASSOC',
  'ASSOCIATES',
  'CAPITAL',
  'DEVELOPMENT',
  'ENTERPRISES',
  'FOUNDATION',
  'GROUP',
  'HOLDINGS',
  'INVESTMENTS',
  'MANAGEMENT',
  'MGMT',
  'PARTNERS',
  'PARTNERSHIP',
  'PROPERTIES',
  'REALTY',
  'SERVICES',
  'VENTURES',
]);

function surnames(name: string) {
  return name
    .replace(/^\((R|E)\)\s*/, '')
    .toUpperCase()
    .split(/[^A-Z]+/)
    .filter((part) => part.length >= 3 && !NAME_NOISE.has(part));
}

/**
 * A deed between the same people (trust funding, name changes, adding a
 * spouse) is not a purchase. Grantor/grantee sharing a surname is treated
 * as such an intra-family transfer.
 */
export function isIntraFamilyTransfer(document: RecorderDocument) {
  const grantors = new Set(
    document.names.filter((name) => !name.startsWith('(E)')).flatMap(surnames),
  );
  const grantees = document.names.filter((name) => name.startsWith('(E)')).flatMap(surnames);
  if (!grantors.size || !grantees.length) return false;
  return grantees.some((part) => grantors.has(part));
}

/**
 * Recording dates of arm's-length deeds and new security instruments. Title
 * insurance and lenders will not close over an open lien, so a lien recorded
 * before any of these was cleared at that transaction, even when the Recorder
 * index never received a matching release.
 */
export function lienClearanceDates(documents: RecorderDocument[]) {
  const times: number[] = [];
  for (const document of documents) {
    const clearing =
      (isTransferDeed(document) && !isIntraFamilyTransfer(document)) ||
      document.titles.some((title) => /^(?:DEED OF TRUST|MORTGAGE)\b/i.test(title.trim()));
    if (!clearing) continue;
    const recorded = documentTime(document.documentDate);
    if (recorded !== null) times.push(recorded);
  }
  return times;
}
